import { formatDueDate, type MyBusiness, type PaymentOnboardingResponse } from "@teamna/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  ErrorState,
  FormRow,
  FormSection,
  InlineError,
  Notice,
  SkeletonList,
  type NoticeTone,
} from "../components/index.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { usePayoutAccount, useRefreshPayoutAccount, useStartPayoutOnboarding } from "@teamna/web-client/hooks/use-payout-account.ts";
import { OfficePage } from "./office.tsx";
import { CardPaymentsComingSoon } from "./card-payments-coming-soon.tsx";

/**
 * Get paid by card: the business's own Stripe account (Stripe Connect, slice 2). Decision record
 * `teamna/decisions/2026-10-05-stripe-connect.md`; prototype `teamna/prototypes/get-paid.html`.
 *
 * The page shows what Teamna last read from Stripe and never decides the state itself. "Start
 * verification" asks the API for a fresh link (it works once and expires in 5 minutes) and opens
 * Stripe's own form, where Stripe checks the business and keeps the documents. Stripe sends the
 * owner back here with `?from=stripe` (the page asks the API to read the account at once) or, for a
 * link that expired, with `?refresh=1` (the page makes a new link and goes straight back).
 *
 * Owner only for the link: the person who fills in Stripe's form decides where the money goes. An
 * admin sees the state.
 *
 * Card payments are not a setup step: members pay by cash and bank transfer without them.
 *
 * While Teamna has card payments off for all businesses (Control Center switch), the page shows
 * "Coming soon" and nothing else, and never opens Stripe.
 */

/** What UAE verification asks for (spike S1, 2026-10-07). The ticks stay in this browser only. */
export const GET_PAID_DOCUMENTS: ReadonlyArray<{ key: string; title: string; hint: string }> = [
  { key: "licence", title: "Trade licence", hint: "A PDF or a clear photo. It must be valid." },
  { key: "moa", title: "Memorandum of Association", hint: "Stripe uses it to see who owns the company." },
  { key: "passport", title: "Passport and Emirates ID of the owner", hint: "Stripe asks for the passport file and the Emirates ID number." },
  { key: "visa", title: "UAE residence visa of the owner", hint: "The visa page, or the e-visa." },
  { key: "authorization", title: "A letter that lets the owner act for the company", hint: "Stripe asks for it also when the owner is the only owner." },
  { key: "bank", title: "Bank letter or statement with the company IBAN", hint: "The account must be in the company name, in AED." },
  { key: "profile", title: "Business address, a support phone, and a website or Instagram page", hint: "Stripe shows the card holder who they pay." },
];

const STRIPE_DASHBOARD_URL = "https://dashboard.stripe.com";

function openInBrowser(url: string): void {
  window.location.assign(url);
}

function readyKey(businessId: string): string {
  return `teamna.getPaid.ready.${businessId}`;
}

function loadReady(businessId: string): string[] {
  try {
    const raw = window.localStorage.getItem(readyKey(businessId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
}

function saveReady(businessId: string, keys: string[]): void {
  try {
    window.localStorage.setItem(readyKey(businessId), JSON.stringify(keys));
  } catch {
    // Private windows refuse storage. The ticks are a help, not a record.
  }
}

/** "Every day, 5 days after each payment", "Manual: no payout unless you start one". */
export function payoutScheduleLine(interval: string | null, delayDays: number | null): string {
  const after = delayDays !== null ? `, ${delayDays} ${delayDays === 1 ? "day" : "days"} after each payment` : "";
  switch (interval) {
    case "daily":
      return `Every working day${after}`;
    case "weekly":
      return `Every week${after}`;
    case "monthly":
      return `Every month${after}`;
    case "manual":
      return "Manual: Stripe sends no money to your bank unless you start a payout";
    default:
      return "Not known yet";
  }
}

/** The box at the top of the page for each state, in plain words. */
export function getPaidHeadline(
  data: PaymentOnboardingResponse,
  timeZone: string,
  /** The business's word for the people who pay, plural and with a capital ("Parents"). */
  members: string,
): { tone: NoticeTone; title: string; body: string } {
  const tasks = data.account?.tasks ?? [];
  const deadline = data.account?.deadline ? formatDueDate(new Date(data.account.deadline), timeZone) : null;
  switch (data.status) {
    case "not_started":
      return {
        tone: "info",
        title: "Connect Stripe to start accepting online payments",
        body: `${members} pay you by cash or bank transfer now. To take cards, Stripe must check your business first. This is the law for card payments in the UAE.`,
      };
    case "pending":
      return tasks.length > 0
        ? { tone: "info", title: "Finish the Stripe form", body: "Stripe still needs some information. Continue where you stopped. Stripe saved what you gave." }
        : {
            tone: "info",
            title: "Stripe is checking your documents",
            body: `This usually takes 1 to 2 business days. This page shows the answer when Stripe has it. ${members} pay by cash or bank transfer until then.`,
          };
    case "needs_information":
      return {
        tone: "warning",
        title: "Stripe needs more information",
        body: data.cardPayments
          ? `Card payments still work${deadline ? ` until ${deadline}` : ""}. Give Stripe the items below${deadline ? ` before ${deadline}` : ""}, or card payments stop.`
          : "Give Stripe the items below. Card payments start when Stripe accepts them.",
      };
    case "complete":
      return { tone: "success", title: "Card payments are on", body: `${members} can pay you by card in the Teamna app. The money goes from Stripe to your bank.` };
    case "restricted":
      return {
        tone: "danger",
        title: "Card payments are stopped",
        body: `Stripe stopped card payments for your business. ${members} pay by cash or bank transfer until Stripe accepts the information below.`,
      };
  }
}

export function OwnerGetPaidScreen({
  returnedFromStripe = false,
  linkExpired = false,
  openUrl = openInBrowser,
  readOnly = false,
  back,
}: {
  /** The link above the title: Settings, where the page opens from in Desk. */
  back?: { to: string; label: string };
  /** Stripe sent the owner back from its form (`?from=stripe`). */
  returnedFromStripe?: boolean;
  /** Stripe's link had expired or was used (`?refresh=1`): make a new one and go back to Stripe. */
  linkExpired?: boolean;
  /** Opens a Stripe page. Tests replace it. */
  openUrl?: (url: string) => void;
  /**
   * Control Center: Teamna staff see the state but never get a Stripe action. Act-as makes the
   * owner the current user, so the role alone cannot tell (review F1.2); the API refuses anyway.
   */
  readOnly?: boolean;
}) {
  const { label } = useLabels();
  const comingSoon = useActiveBusiness().business?.cardPaymentsComingSoon === true;
  return (
    <OfficePage
      {...(back ? { back } : {})}
      title="Get paid by card"
      description={
        comingSoon
          ? "Card payments in Teamna are not available yet."
          : `${label("member", { plural: true, capital: true })} pay you by card in the app. The money goes from Stripe to your bank. Teamna takes no fee.`
      }
    >
      {(business) => (
        <GetPaidBody business={business} returnedFromStripe={returnedFromStripe} linkExpired={linkExpired} openUrl={openUrl} readOnly={readOnly} />
      )}
    </OfficePage>
  );
}

function GetPaidBody({
  business,
  returnedFromStripe,
  linkExpired,
  openUrl,
  readOnly,
}: {
  business: MyBusiness;
  returnedFromStripe: boolean;
  linkExpired: boolean;
  openUrl: (url: string) => void;
  readOnly: boolean;
}) {
  const businessId = business.businessId;
  const comingSoon = business.cardPaymentsComingSoon === true;
  const isOwner = business.staffRole === "owner" && !readOnly;
  const { label } = useLabels();
  const account = usePayoutAccount(businessId);
  const start = useStartPayoutOnboarding(businessId);
  const refresh = useRefreshPayoutAccount(businessId);
  const [ready, setReady] = useState<string[]>(() => loadReady(businessId));

  // `mutate` is stable for the life of the page, so this is too.
  const startMutate = start.mutate;
  const refreshMutate = refresh.mutate;
  const openStripe = useCallback(
    () =>
      startMutate(undefined, {
        onSuccess: (result) => {
          if (result.onboardingUrl) openUrl(result.onboardingUrl);
        },
      }),
    [startMutate, openUrl],
  );

  // Each return from Stripe runs its action once, also when React mounts the page twice.
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    if (returnedFromStripe) {
      handled.current = true;
      refreshMutate();
    } else if (linkExpired && isOwner && !comingSoon) {
      handled.current = true;
      openStripe();
    }
    // Once per page visit: the ref keeps a later render from asking Stripe again.
  }, [returnedFromStripe, linkExpired, isOwner, comingSoon, openStripe, refreshMutate]);

  if (comingSoon || account.data?.comingSoon) return <CardPaymentsComingSoon />;
  if (account.isLoading && !account.data) return <SkeletonList rows={3} />;
  if (account.error && !account.data) return <ErrorState error={account.error} onRetry={() => void account.refetch()} />;
  const data = account.data!;

  // The stub provider is its own state: no Stripe, no verification, nothing "on" (review F1.1).
  if (data.provider === "fake") {
    return (
      <div className="flex flex-col gap-6">
        <Notice tone="info" title="Card payments are not connected yet">
          Stripe is not connected on this server. The card checkout is a test page and no money moves. Cash and bank transfer work as usual.
        </Notice>
      </div>
    );
  }

  const headline = getPaidHeadline(data, business.timezone, label("member", { plural: true, capital: true }));
  const tasks = data.account?.tasks ?? [];
  const canAct = isOwner && data.status !== "complete";
  const actionLabel =
    data.status === "not_started" ? "Start verification" : tasks.length === 0 ? null : data.status === "pending" ? "Continue on Stripe" : "Fix it on Stripe";

  const toggle = (key: string, checked: boolean) => {
    const next = checked ? [...new Set([...ready, key])] : ready.filter((value) => value !== key);
    setReady(next);
    saveReady(businessId, next);
  };

  return (
    <div className="flex flex-col gap-6">
      {linkExpired && isOwner && start.isPending ? <Notice tone="info" title="Opening Stripe again">Your last Stripe link expired. We are making a new one.</Notice> : null}
      {start.error ? <InlineError error={start.error} /> : null}
      {refresh.error ? <InlineError error={refresh.error} /> : null}

      <Notice
        tone={headline.tone}
        title={headline.title}
        action={
          canAct && actionLabel ? (
            <Button loading={start.isPending} onClick={openStripe}>
              {actionLabel}
            </Button>
          ) : undefined
        }
      >
        <p>{headline.body}</p>
        {tasks.length > 0 && data.status !== "not_started" ? (
          <ul className="mt-2 list-disc pl-5">
            {tasks.map((task) => (
              <li key={task}>{task}</li>
            ))}
          </ul>
        ) : null}
      </Notice>

      {!isOwner && data.status !== "complete" ? (
        <Notice tone="info" title="Only the owner can connect the payment account">
          {readOnly
            ? "Teamna staff can see this page. The person who fills in the Stripe form decides where the money goes, so only the owner does this step."
            : "The person who fills in the Stripe form decides where the money goes. Ask the owner of the business to do this step."}
        </Notice>
      ) : null}

      {data.status === "not_started" ? (
        <FormSection
          title="What you need"
          description={`Get these ready before you start. Then it takes about 10 minutes. ${ready.length} of ${GET_PAID_DOCUMENTS.length} ready.`}
        >
          <div className="flex flex-col gap-1 py-3">
            {GET_PAID_DOCUMENTS.map((doc) => (
              <Checkbox
                key={doc.key}
                label={doc.title}
                hint={doc.hint}
                checked={ready.includes(doc.key)}
                onChange={(checked) => toggle(doc.key, checked)}
              />
            ))}
          </div>
          <p className="border-t border-line py-4 text-[14px] text-ink-2">
            You give these to Stripe on Stripe&apos;s own page. Teamna does not see or keep them. Your business must be a company with a trade licence: an LLC, a sole establishment or a free zone company. Stripe asks you to make a Stripe login, and saves your progress if you stop.
          </p>
        </FormSection>
      ) : null}

      {data.account && data.status !== "not_started" ? (
        <FormSection title="Money goes to" description={`What Stripe told Teamna on ${formatDateTime(data.account.checkedAt, business.timezone)}.`}>
          <FormRow label="Business">
            <p className="text-[14px]" dir="auto">{data.account.legalName ?? "Not given yet"}</p>
          </FormRow>
          <FormRow label="Bank">
            <p className="text-[14px]">
              {data.account.bankLast4 ? `${data.account.bankName ?? "Bank"}, IBAN ends in ${data.account.bankLast4}` : "Not given yet"}
            </p>
          </FormRow>
          <FormRow label="Payouts">
            <p className="text-[14px]">{payoutScheduleLine(data.account.payoutInterval, data.account.payoutDelayDays)}</p>
          </FormRow>
          <FormRow label="Fees" hint="On a refund, Stripe keeps its fee.">
            <p className="text-[14px]">Stripe takes its card fee from each payment. Teamna takes no fee.</p>
          </FormRow>
          <div className="flex flex-wrap items-center gap-3 py-4">
            {readOnly ? null : (
              <Button variant="secondary" loading={refresh.isPending} onClick={() => refresh.mutate()}>
                Check again
              </Button>
            )}
            {data.status !== "pending" && !readOnly ? (
              <a className="text-[14px] font-semibold text-accent-strong underline" href={STRIPE_DASHBOARD_URL} target="_blank" rel="noreferrer">
                Open my Stripe dashboard
              </a>
            ) : null}
          </div>
        </FormSection>
      ) : null}

      {data.account?.payoutInterval === "manual" ? (
        <Notice tone="warning" title="Payouts are manual">
          Stripe sends no money to your bank by itself. In your Stripe dashboard, set the payout schedule to daily or weekly, or start each payout there.
        </Notice>
      ) : null}

      {data.verifiedAt && data.status === "complete" ? (
        <p className="text-[13px] text-ink-2">Verified by Stripe on {formatDueDate(new Date(data.verifiedAt), business.timezone)}.</p>
      ) : null}
    </div>
  );
}
